import React, { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Drawer,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  AuditOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  PlusOutlined,
  RollbackOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography

// ── Types ─────────────────────────────────────────────────────────────────────

interface IssueSlip {
  id: string
  issue_slip_no: string
  laydown_id: string | null
  laydown?: { laydown_no: string; count_code?: string }
  issue_date: string
  total_bales: number
  total_weight_kg: string
  issued_bales: number
  issued_weight_kg: string
  returned_bales: number
  returned_weight_kg: string
  pending_bales: number
  net_issued_kg: string
  status: 'DRAFT' | 'ISSUED' | 'CLOSED'
  remarks?: string
}

interface SlipBale {
  id: string
  bale_no: string
  lot_no?: string
  weight_kg: string
  godown?: string
  line_status: string
}

interface IssueSlipDetail extends IssueSlip {
  bales: SlipBale[]
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const statusColor: Record<string, string> = {
  DRAFT: 'orange',
  ISSUED: 'blue',
  CLOSED: 'green',
}

function fmtKg(v: string | number | null | undefined) {
  if (v === null || v === undefined) return '—'
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: 3 })
}

// ── Return Modal ──────────────────────────────────────────────────────────────

function ReturnBalesModal({
  slip,
  open,
  onClose,
  onSuccess,
}: {
  slip: IssueSlipDetail | null
  open: boolean
  onClose: () => void
  onSuccess: () => void
}) {
  const [form] = Form.useForm()
  const [selectedBaleIds, setSelectedBaleIds] = useState<string[]>([])
  const qc = useQueryClient()

  const returnMut = useMutation({
    mutationFn: (d: { bale_ids: string[]; reason: string; notes?: string }) =>
      apiClient.post(`/issue-slips/${slip!.id}/return-bales`, d).then(r => r.data),
    onSuccess: () => {
      message.success('Bales returned successfully')
      qc.invalidateQueries({ queryKey: ['issue-slips'] })
      onSuccess()
      onClose()
      form.resetFields()
      setSelectedBaleIds([])
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed to return bales'),
  })

  const issuedBales = (slip?.bales ?? []).filter(b => b.line_status === 'ISSUED')

  const handleSubmit = () => {
    form.validateFields().then(vals => {
      if (selectedBaleIds.length === 0) {
        message.warning('Select at least one bale to return')
        return
      }
      returnMut.mutate({ bale_ids: selectedBaleIds, reason: vals.reason, notes: vals.notes })
    })
  }

  const baleCols = [
    {
      title: '',
      key: 'check',
      width: 40,
      render: (_: unknown, r: SlipBale) => (
        <Checkbox
          checked={selectedBaleIds.includes(r.id)}
          onChange={e => {
            if (e.target.checked) setSelectedBaleIds(p => [...p, r.id])
            else setSelectedBaleIds(p => p.filter(id => id !== r.id))
          }}
        />
      ),
    },
    { title: 'Bale No', dataIndex: 'bale_no', key: 'bn', render: (v: string) => <Text code>{v}</Text> },
    { title: 'Lot No', dataIndex: 'lot_no', key: 'ln', render: (v?: string) => v || '—' },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight_kg',
      key: 'wt',
      align: 'right' as const,
      render: (v: string) => Number(v).toFixed(3),
    },
    { title: 'Godown', dataIndex: 'godown', key: 'gd', render: (v?: string) => v || '—' },
  ]

  return (
    <Modal
      title={
        <Space>
          <RollbackOutlined style={{ color: '#fa8c16' }} />
          Return Excess Bales — {slip?.issue_slip_no}
        </Space>
      }
      open={open}
      onCancel={() => {
        onClose()
        form.resetFields()
        setSelectedBaleIds([])
      }}
      onOk={handleSubmit}
      okText={`Return ${selectedBaleIds.length} Bale(s)`}
      confirmLoading={returnMut.isPending}
      width={720}
      okButtonProps={{ disabled: selectedBaleIds.length === 0 }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          type="info"
          showIcon
          message={`Select the bales to return. ${issuedBales.length} issued bales available for return.`}
        />

        <Row justify="space-between" align="middle">
          <Space>
            <Button
              size="small"
              onClick={() => setSelectedBaleIds(issuedBales.map(b => b.id))}
            >
              Select All ({issuedBales.length})
            </Button>
            <Button size="small" onClick={() => setSelectedBaleIds([])}>
              Clear
            </Button>
          </Space>
          {selectedBaleIds.length > 0 && (
            <Tag color="orange">{selectedBaleIds.length} selected</Tag>
          )}
        </Row>

        <Table
          dataSource={issuedBales}
          columns={baleCols}
          rowKey="id"
          size="small"
          pagination={issuedBales.length > 10 ? { pageSize: 10 } : false}
          scroll={{ y: 240 }}
        />

        <Form form={form} layout="vertical" size="small">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="reason"
                label="Return Reason"
                rules={[{ required: true, message: 'Select a reason' }]}
              >
                <Select
                  options={[
                    { value: 'EXCESS', label: 'Excess — more than required' },
                    { value: 'DAMAGE', label: 'Damage — bale damaged' },
                    { value: 'WRONG_LOT', label: 'Wrong Lot — incorrect lot issued' },
                    { value: 'OTHER', label: 'Other' },
                  ]}
                  placeholder="Select reason"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="notes" label="Notes (optional)">
                <Select
                  mode="tags"
                  placeholder="Optional notes"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Space>
    </Modal>
  )
}

// ── View Drawer ───────────────────────────────────────────────────────────────

function SlipDetailDrawer({
  slipId,
  open,
  onClose,
  onReturnClick,
}: {
  slipId: string | null
  open: boolean
  onClose: () => void
  onReturnClick: (slip: IssueSlipDetail) => void
}) {
  const { data: slip, isLoading } = useQuery<IssueSlipDetail>({
    queryKey: ['issue-slip-detail', slipId],
    queryFn: () => apiClient.get(`/issue-slips/${slipId}`).then(r => r.data),
    enabled: !!slipId && open,
  })

  const baleCols = [
    { title: 'Bale No', dataIndex: 'bale_no', key: 'bn', render: (v: string) => <Text code>{v}</Text> },
    { title: 'Lot No', dataIndex: 'lot_no', key: 'ln', render: (v?: string) => v || '—' },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight_kg',
      key: 'wt',
      align: 'right' as const,
      render: (v: string) => Number(v).toFixed(3),
    },
    { title: 'Godown', dataIndex: 'godown', key: 'gd', render: (v?: string) => v || '—' },
    {
      title: 'Status',
      dataIndex: 'line_status',
      key: 'st',
      render: (v: string) => (
        <Tag color={v === 'ISSUED' ? 'blue' : v === 'RETURNED' ? 'green' : 'default'}>{v}</Tag>
      ),
    },
  ]

  return (
    <Drawer
      title={
        <Space>
          <FileTextOutlined />
          Issue Slip Detail
          {slip && <Tag color={statusColor[slip.status]}>{slip.status}</Tag>}
        </Space>
      }
      open={open}
      onClose={onClose}
      width={700}
      loading={isLoading}
      extra={
        slip?.status === 'ISSUED' ? (
          <Button
            type="primary"
            icon={<RollbackOutlined />}
            danger
            onClick={() => slip && onReturnClick(slip)}
          >
            Return Excess Bales
          </Button>
        ) : null
      }
    >
      {slip && (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Slip No" span={2}>
              <Text strong style={{ fontFamily: 'monospace' }}>{slip.issue_slip_no}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Laydown">
              {slip.laydown?.laydown_no ?? '—'}
              {slip.laydown?.count_code && (
                <Tag color="purple" style={{ marginLeft: 6 }}>{slip.laydown.count_code}</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Issue Date">
              {dayjs(slip.issue_date).format('DD-MMM-YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Total Bales">{slip.total_bales}</Descriptions.Item>
            <Descriptions.Item label="Total Weight">{fmtKg(slip.total_weight_kg)} kg</Descriptions.Item>
            <Descriptions.Item label="Issued">{slip.issued_bales} bales</Descriptions.Item>
            <Descriptions.Item label="Issued Weight">{fmtKg(slip.issued_weight_kg)} kg</Descriptions.Item>
            <Descriptions.Item label="Returned">{slip.returned_bales} bales</Descriptions.Item>
            <Descriptions.Item label="Returned Weight">{fmtKg(slip.returned_weight_kg)} kg</Descriptions.Item>
            <Descriptions.Item label="Pending">{slip.pending_bales} bales</Descriptions.Item>
            <Descriptions.Item label="Net Issued">{fmtKg(slip.net_issued_kg)} kg</Descriptions.Item>
            {slip.remarks && (
              <Descriptions.Item label="Remarks" span={2}>{slip.remarks}</Descriptions.Item>
            )}
          </Descriptions>

          <Card size="small" title={<><UnorderedListOutlined /> Bale List ({slip.bales?.length ?? 0})</>}>
            <Table
              dataSource={slip.bales ?? []}
              columns={baleCols}
              rowKey="id"
              size="small"
              pagination={slip.bales?.length > 20 ? { pageSize: 20 } : false}
              scroll={{ x: 500 }}
            />
          </Card>
        </Space>
      )}
    </Drawer>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function IssueSlipPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [viewSlipId, setViewSlipId] = useState<string | null>(null)
  const [returnSlip, setReturnSlip] = useState<IssueSlipDetail | null>(null)

  const { data: slips, isLoading } = useQuery<IssueSlip[]>({
    queryKey: ['issue-slips', plantId],
    queryFn: () =>
      apiClient.get('/issue-slips', { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const list = Array.isArray(slips) ? slips : []

  // KPI calculations
  const openSlips = list.filter(s => s.status === 'ISSUED').length
  const today = dayjs().format('YYYY-MM-DD')
  const issuedTodayBales = list
    .filter(s => dayjs(s.issue_date).format('YYYY-MM-DD') === today)
    .reduce((sum, s) => sum + (s.issued_bales ?? 0), 0)
  const pendingReturns = list.reduce((sum, s) => sum + (s.pending_bales ?? 0), 0)

  const columns = [
    {
      title: 'Slip No',
      dataIndex: 'issue_slip_no',
      key: 'slip_no',
      width: 160,
      render: (v: string) => <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text>,
    },
    {
      title: 'Laydown',
      key: 'laydown',
      width: 160,
      render: (_: unknown, r: IssueSlip) =>
        r.laydown ? (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }}>{r.laydown.laydown_no}</Text>
            {r.laydown.count_code && <Tag color="purple">{r.laydown.count_code}</Tag>}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Date',
      dataIndex: 'issue_date',
      key: 'date',
      width: 120,
      render: (v: string) => dayjs(v).format('DD-MMM-YYYY'),
    },
    {
      title: 'Total Bales',
      dataIndex: 'total_bales',
      key: 'total',
      width: 90,
      align: 'right' as const,
      render: (v: number) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Issued',
      dataIndex: 'issued_bales',
      key: 'issued',
      width: 80,
      align: 'right' as const,
      render: (v: number) => <Text strong style={{ color: '#1677ff' }}>{v}</Text>,
    },
    {
      title: 'Returned',
      dataIndex: 'returned_bales',
      key: 'returned',
      width: 85,
      align: 'right' as const,
      render: (v: number) => v > 0 ? <Text style={{ color: '#52c41a' }}>{v}</Text> : <Text type="secondary">0</Text>,
    },
    {
      title: 'Pending',
      dataIndex: 'pending_bales',
      key: 'pending',
      width: 80,
      align: 'right' as const,
      render: (v: number) => v > 0 ? <Tag color="orange">{v}</Tag> : <Text type="secondary">0</Text>,
    },
    {
      title: 'Net Issued (kg)',
      dataIndex: 'net_issued_kg',
      key: 'net',
      width: 130,
      align: 'right' as const,
      render: (v: string) => <Text strong>{fmtKg(v)}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v: 'DRAFT' | 'ISSUED' | 'CLOSED') => (
        <Tag color={statusColor[v]}>{v}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, r: IssueSlip) => (
        <Space size={4}>
          <Button size="small" icon={<FileTextOutlined />} onClick={() => setViewSlipId(r.id)}>
            View
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <AuditOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Issue Slips
      </Title>
      <Text type="secondary">Track bale issue slips from laydowns — issued, returned, and pending bales</Text>

      {/* KPI Cards */}
      <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Open Slips"
              value={openSlips}
              valueStyle={{ color: '#1677ff' }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Total Bales Issued Today"
              value={issuedTodayBales}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Pending Returns"
              value={pendingReturns}
              valueStyle={{ color: pendingReturns > 0 ? '#fa8c16' : '#52c41a' }}
              prefix={<RollbackOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Select
            value={plantId}
            onChange={setPlantId}
            style={{ width: 200 }}
            options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
          />
          <Button type="primary" icon={<PlusOutlined />} disabled>
            New Issue Slip
          </Button>
        </Row>

        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 20, showTotal: t => `${t} slips` }}
          scroll={{ x: 900 }}
        />
      </Card>

      {/* View Drawer */}
      <SlipDetailDrawer
        slipId={viewSlipId}
        open={!!viewSlipId}
        onClose={() => setViewSlipId(null)}
        onReturnClick={slip => {
          setReturnSlip(slip)
          setViewSlipId(null)
        }}
      />

      {/* Return Modal */}
      <ReturnBalesModal
        slip={returnSlip}
        open={!!returnSlip}
        onClose={() => setReturnSlip(null)}
        onSuccess={() => setReturnSlip(null)}
      />
    </div>
  )
}
